import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { theme as ipcTheme } from '@ipc/ipc-components';
import colors from './colors';

declare module '@mui/material/styles' {
  interface Palette {
    blue: Palette['primary'];
    checkboxColor: {
      main: string;
    };
    focused: {
      main: string;
    };
  }
  interface PaletteOptions {
    blue?: PaletteOptions['primary'];
    checkboxColor?: {
      main: string;
    };
    focused?: {
      main: string;
    };
  }
}

// Create base theme with IPC theme as foundation
const baseTheme = createTheme(ipcTheme);

// Create theme options
const themeOptions = {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarWidth: 'thin', // Affects Firefox
          scrollbarColor: '#063142 #9CADB4', // Affects Firefox
          '&::-webkit-scrollbar': {
            width: '7px',
            backgroundColor: '#9CADB4',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#9CADB4',
            boxShadow: 'inset 0 0 6px rgba(83, 83, 83, 0.07)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#063142',
            '&:hover': {
              backgroundColor: '#094259',
            },
          },
          '&::-webkit-scrollbar-button': {
            display: 'none !important', // **Use display: none instead of width: 0px**
          },
        },
      },
    },
  },
  palette: {
    ...baseTheme.palette,
    mode: 'light' as const,
    primary: {
      main: colors.primary || '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary || '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#ffffff',
    },
    error: {
      main: colors.danger || '#d32f2f',
      light: colors.dangerLight || '#ef5350',
      dark: colors.dangerDark || '#c62828',
      contrastText: '#ffffff',
    },
    warning: {
      main: colors.warning || '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#ffffff',
    },
    info: {
      main: colors.info || '#0288d1',
      light: colors.infoLight || '#03a9f4',
      dark: '#01579b',
      contrastText: '#ffffff',
    },
    success: {
      main: colors.success || '#2e7d32',
      light: colors.successLight || '#4caf50',
      dark: colors.successDark || '#1b5e20',
      contrastText: '#ffffff',
    },
    background: {
      paper: colors.white || '#FFFFFF',
      default: colors.grayLightest || '#FAFAFA',
    },
    blue: {
      main: colors.basic?.blue || '#0288d1',
      light: colors.blueLight || '#03a9f4',
      dark: '#01579b',
      contrastText: '#ffffff',
    },
    checkboxColor: {
      main: colors.primary || '#1976d2',
    },
    focused: {
      main: colors.focus || '#1976d2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 400,
      textTransform: 'uppercase',
    },
  },
} as const;

// Create final theme with merged options
export const muiTheme = createTheme({
  ...baseTheme,
  ...themeOptions,
} as ThemeOptions);

export type AppTheme = Theme;

// Create styled-components theme by extending IPC theme
export const styledTheme = {
  ...ipcTheme,
  colors: {
    ...ipcTheme.colors,
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.danger,
    white: colors.white,
    black: colors.black,
    gray: {
      light: colors.grayLight,
      main: colors.secondary,
      dark: colors.secondaryDark,
    },
  },
  spacing: (factor: number) => `${8 * factor}px`,
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '960px',
    lg: '1280px',
    xl: '1920px',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: {
      small: '0.875rem',
      medium: '1rem',
      large: '1.25rem',
      h1: '2.5rem',
      h2: '2rem',
      h3: '1.75rem',
      h4: '1.5rem',
      h5: '1.25rem',
      h6: '1rem',
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
    },
  },
};
